import {
  SUBMISSION_STAGE_STATE,
  type RunPreparationSummary,
  type SubmissionProgress,
} from "@nova-ai/shared"
import { useId, useLayoutEffect, useRef, useState, type KeyboardEvent, type ReactNode } from "react"

import { RUN_PAGE_TAB, type RunPageTab } from "../../lib/run-page-tab"
import { cn } from "../../lib/utils"
import { useRunSubmissionState } from "../../services/use-run-submission-state"
import { SegmentedControl } from "../ui/segmented-control"
import { PortalContainerContext } from "../ui/portal-container"
import { RunSubmissionProgress } from "./run-submission-tracker"

const TABS = Object.values(RUN_PAGE_TAB)

export function RunPageTabs({
  slug,
  configCount,
  preparation,
  tab,
  onTabChange,
  sharedWarnings,
  metadata,
  children,
}: {
  slug: string
  configCount: number
  preparation: RunPreparationSummary | null | undefined
  tab: RunPageTab
  onTabChange: (tab: RunPageTab) => void
  sharedWarnings: ReactNode
  metadata: ReactNode
  children: ReactNode
}) {
  const submission = useRunSubmissionState(slug)
  const id = useId()
  const root = useRef<HTMLDivElement>(null)
  const previousTab = useRef(tab)
  const status = workflowStatus(submission.progress, submission.error)

  useLayoutEffect(() => {
    if (previousTab.current === tab) {
      return
    }
    previousTab.current = tab
    const focused = document.activeElement
    const hiddenPanel = root.current?.querySelector('[role="tabpanel"][hidden]')
    if (focused === document.body || (focused && hiddenPanel?.contains(focused))) {
      root.current?.querySelector<HTMLButtonElement>('[role="tab"][aria-selected="true"]')?.focus()
    }
  }, [tab])

  function onKeyDown(event: KeyboardEvent<HTMLButtonElement>, current: RunPageTab) {
    let next: RunPageTab | undefined
    if (event.key === "Home") {
      next = RUN_PAGE_TAB.products
    }
    if (event.key === "End") {
      next = RUN_PAGE_TAB.workflow
    }
    if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
      const direction = event.key === "ArrowRight" ? 1 : -1
      next = TABS[(TABS.indexOf(current) + direction + TABS.length) % TABS.length]
    }
    if (!next) {
      return
    }
    event.preventDefault()
    root.current?.querySelector<HTMLButtonElement>(`#${CSS.escape(`${id}-${next}-tab`)}`)?.focus()
    onTabChange(next)
  }

  return (
    <div ref={root} className="flex min-h-0 min-w-0 flex-1 flex-col gap-3">
      <div className="flex shrink-0 flex-wrap items-center justify-between gap-x-1 gap-y-2 border-b border-border pb-2 sm:gap-x-4">
        <SegmentedControl
          label="Run workspace"
          value={tab}
          onChange={onTabChange}
          tabs={{ idPrefix: id, onKeyDown }}
          options={[
            {
              value: RUN_PAGE_TAB.products,
              label: (
                <>
                  <span>Products</span>

                  <span className="tabular-nums text-muted-foreground">
                    ({configCount.toLocaleString("en-US")} selected)
                  </span>
                </>
              ),
            },
            {
              value: RUN_PAGE_TAB.workflow,
              label: (
                <span>
                  Workflow
                  <span
                    role="status"
                    className={cn("block text-xs font-normal", status.attention && "text-warning")}
                  >
                    {status.label}
                  </span>
                </span>
              ),
            },
          ]}
        />

        {metadata}
      </div>

      {sharedWarnings}

      <RunPagePanel id={id} value={RUN_PAGE_TAB.products} active={tab === RUN_PAGE_TAB.products}>
        {children}
      </RunPagePanel>

      <RunPagePanel id={id} value={RUN_PAGE_TAB.workflow} active={tab === RUN_PAGE_TAB.workflow}>
        <RunSubmissionProgress {...submission} preparation={preparation} />
      </RunPagePanel>
    </div>
  )
}

function RunPagePanel({
  id,
  value,
  active,
  children,
}: {
  id: string
  value: RunPageTab
  active: boolean
  children: ReactNode
}) {
  const [container, setContainer] = useState<HTMLDivElement | null>(null)
  return (
    <div
      ref={setContainer}
      role="tabpanel"
      id={`${id}-${value}-panel`}
      aria-labelledby={`${id}-${value}-tab`}
      hidden={!active}
      inert={!active}
      tabIndex={active ? 0 : -1}
      className={cn(
        "min-h-0 min-w-0 flex-1 flex-col gap-3 rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-ring",
        active ? "flex" : "hidden"
      )}
    >
      <PortalContainerContext value={container}>{children}</PortalContainerContext>
    </div>
  )
}

function workflowStatus(progress: SubmissionProgress | undefined, error: Error | null) {
  const attention = progress
    ? Object.values(progress.stages).some(
        (stage) =>
          stage.state === SUBMISSION_STAGE_STATE.blocked ||
          stage.state === SUBMISSION_STAGE_STATE.failed
      )
    : false
  if (error) {
    return { attention, label: attention ? "Needs attention · Unavailable" : "Unavailable" }
  }
  if (!progress) {
    return { attention: false, label: "Loading…" }
  }
  return { attention, label: attention ? "Needs attention" : null }
}
